"use client";

import { useSearchParams } from "next/navigation";
import { useLocale, useTranslations } from "next-intl";
import { BackButtonHeader, PageWrapper } from "@/components/core";
import { InfiniteScroll } from "@/components/core/InfiniteScroll/InfiniteScroll";
import { ShopItemCard } from "@/components/features/shop/ShopDetail/ShopItemCard";
import { Skeleton } from "@/components/ui";
import { useFetchSearchProducts } from "@/hooks";
import { $api } from "@/lib/api-client";
import { localizeName } from "@/lib/i18n";
import { hasLoadedListItems } from "@/lib/pagination/hasLoadedListItems";
import { cn } from "@/lib/utils";

const ProductItemSkeleton = ({ className }: { className?: string }) => (
  <div
    className={cn(
      "flex items-center gap-5 border-t border-b border-stroke-subtle py-5 px-4",
      className,
    )}
  >
    <Skeleton variant="muted" className="size-[140px] rounded-sm" />
    <div className="flex-1 space-y-2">
      <Skeleton variant="muted" className="h-4 w-20" />
      <Skeleton variant="muted" className="h-3 w-full" />
      <Skeleton variant="muted" className="h-3 w-3/4" />
    </div>
  </div>
);

const SearchPage = () => {
  const t = useTranslations("errors");
  const locale = useLocale();
  const searchParams = useSearchParams();
  const search = searchParams.get("q") || undefined;
  const category_id = searchParams.get("category_id") || undefined;
  const parent_category_id =
    searchParams.get("parent_category_id") || undefined;

  // Fetch parent category if parent_category_id exists
  const parentCategory = $api.useQuery(
    "get",
    "/category/{id}",
    { params: { path: { id: parent_category_id || "" } } },
    { enabled: !!parent_category_id },
  );

  const products = useFetchSearchProducts({
    search,
    category_id,
    parent_category_id,
    limit: 10,
  });

  // Determine the header title
  const headerTitle =
    localizeName(parentCategory.data?.data?.name, locale) ||
    search ||
    t("searchFallback");

  const allProducts = products.data?.pages.flatMap(
    (page) => page?.data?.result || [],
  );

  return (
    <PageWrapper
      containerClassName="pt-0 px-0"
      outsideChildren={
        <BackButtonHeader className="p-4 pb-0" title={headerTitle} />
      }
    >
      {/* Error state */}
      {products.error && !hasLoadedListItems(products) && (
        <div className="text-center text-semantic-error py-8 px-4">
          <p>{t("loadError")}</p>
          <p className="text-sm mt-2">
            {products.error &&
            typeof products.error === "object" &&
            products.error !== null &&
            "message" in products.error
              ? String(products.error.message)
              : t("unknownError")}
          </p>
        </div>
      )}

      {/* Empty state */}
      {!(products.isLoading && !hasLoadedListItems(products)) &&
        !products.error &&
        (!allProducts || allProducts.length === 0) && (
          <div className="text-center text-text-secondary py-12 px-4">
            <p className="text-base">{t("productNotFound")}</p>
            {search && (
              <p className="text-sm mt-2">
                {t("noResults", { query: search })}
              </p>
            )}
          </div>
        )}

      {/* Products list */}
      {!products.error && (
        <>
          {/* List of products */}
          <div>
            {products.isLoading && !hasLoadedListItems(products)
              ? Array.from({ length: 5 }).map((_, i) => (
                  <ProductItemSkeleton key={`skeleton-${i + 1}`} />
                ))
              : allProducts?.map((item) => (
                  <ShopItemCard
                    className="border-t-0 border-stroke-subtle"
                    key={item.id}
                    {...item}
                  />
                ))}
          </div>

          {/* Infinite scroll trigger */}
          <InfiniteScroll query={products} className="py-4" />
        </>
      )}
    </PageWrapper>
  );
};

export default SearchPage;
